import { useState, useRef, useEffect } from 'react';
import {
  IoSearch,
  IoClose,
  IoChevronBack,
  IoCalendarOutline,
  IoFilterOutline,
  IoFunnelOutline
} from 'react-icons/io5';
import DateField from '../forms/DateField';
import CustomSelect from '../forms/CustomSelect';

const ArticleFilters = ({ onFilterChange, onSearchChange, loading = false }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  const filtersSidebarRef = useRef(null);
  const desktopFilterBtnRef = useRef(null);
  const mobileFilterBtnRef = useRef(null);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearchChange(value);
  };

  const handleStatusChange = (e) => {
    const status = e.target.value;
    setSelectedStatus(status);
    applyFilters(status, dateRange);
  };

  const handleDateChange = (field, value) => {
    setDateRange((prev) => {
      const newDateRange = { ...prev, [field]: value };
      applyFilters(selectedStatus, newDateRange);
      return newDateRange;
    });
  };

  const clearFilters = () => {
    setSelectedStatus('');
    setDateRange({ from: '', to: '' });
    applyFilters('', { from: '', to: '' });
  };

  const applyFilters = (status, dates) => {
    onFilterChange({
      status,
      dateRange: dates
    });
  };

  // ✅ FIX: avoid stale showFilters value causing sticky/open state issues
  const toggleFilters = () => {
    setShowFilters((prev) => {
      const next = !prev;
      document.body.style.overflow = next ? 'hidden' : '';
      return next;
    });
  };

  // ✅ Close sidebar + reset overflow helper
  const closeFilters = () => {
    setShowFilters(false);
    document.body.style.overflow = '';
  };

  // Handle click outside to close the filters sidebar (all screen sizes)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const sidebar = filtersSidebarRef.current;
      const desktopBtn = desktopFilterBtnRef.current;
      const mobileBtn = mobileFilterBtnRef.current;

      const clickedInsideSidebar = sidebar && sidebar.contains(event.target);
      const clickedDesktopBtn = desktopBtn && desktopBtn.contains(event.target);
      const clickedMobileBtn = mobileBtn && mobileBtn.contains(event.target);

      // If click is outside sidebar and outside BOTH toggle buttons -> close
      if (!clickedInsideSidebar && !clickedDesktopBtn && !clickedMobileBtn) {
        closeFilters();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, []);

  // Calculate active filters count
  const activeFiltersCount =
    (selectedStatus ? 1 : 0) + (dateRange.from ? 1 : 0) + (dateRange.to ? 1 : 0);

  const hasFilters = activeFiltersCount > 0;

  return (
    <div className="relative">
      {/* Top bar (sticky below fixed navbar) */}
      <div className="sticky top-20 z-30">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-slate-200 bg-white/85 p-3 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              {/* Search */}
              <div className="relative w-full md:flex-1">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <IoSearch className="h-5 w-5" />
                </span>

                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className={[
                    'h-11 w-full rounded-xl border bg-white pl-10 pr-10 text-sm',
                    'border-slate-200 text-slate-900 placeholder:text-slate-400',
                    'focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-400',
                    'dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-sky-500/30 dark:focus:border-sky-500/50',
                    loading ? 'opacity-75' : ''
                  ].join(' ')}
                />

                {searchTerm && (
                  <button
                    type="button"
                    className={[
                      'absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2',
                      'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
                      'dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-slate-200'
                    ].join(' ')}
                    onClick={() => {
                      setSearchTerm('');
                      onSearchChange('');
                    }}
                    aria-label="Clear search"
                  >
                    <IoClose className="h-5 w-5" />
                  </button>
                )}
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between gap-2 md:justify-end">
                <div className="md:hidden text-xs text-slate-500 dark:text-slate-400">
                  {loading ? 'Loading…' : hasFilters ? `${activeFiltersCount} active filter(s)` : 'No filters'}
                </div>

                {/* Desktop filters toggle (ref included ✅) */}
                <button
                  ref={desktopFilterBtnRef}
                  className={[
                    'hidden md:inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition',
                    'border-slate-200 bg-white text-slate-900 hover:bg-slate-50',
                    'focus:outline-none focus:ring-2 focus:ring-sky-500/40',
                    'dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-900',
                    showFilters ? 'ring-2 ring-sky-500/25 dark:ring-sky-500/20' : '',
                    hasFilters ? 'border-sky-300/70 dark:border-sky-700/50' : ''
                  ].join(' ')}
                  onClick={toggleFilters}
                  type="button"
                >
                  <IoFilterOutline className="h-5 w-5 text-sky-600 dark:text-sky-400" />
                  <span>Filters</span>
                  {hasFilters && (
                    <span className="ml-1 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-sky-600 px-2 text-xs font-semibold text-white">
                      {activeFiltersCount}
                    </span>
                  )}
                </button>

                {/* Quick reset (md+) */}
                <button
                  type="button"
                  onClick={clearFilters}
                  disabled={!selectedStatus && !dateRange.from && !dateRange.to}
                  className={[
                    'hidden md:inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium transition',
                    'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    'dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-slate-100'
                  ].join(' ')}
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Mobile chips */}
            {(hasFilters || dateRange.from || dateRange.to || selectedStatus) && (
              <div className="mt-3 flex flex-wrap items-center gap-2 md:hidden">
                {selectedStatus && (
                  <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-medium text-sky-800 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-200">
                    Status: {selectedStatus}
                    <button
                      type="button"
                      className="rounded-full p-1 hover:bg-sky-100 dark:hover:bg-slate-900"
                      onClick={() => {
                        setSelectedStatus('');
                        applyFilters('', dateRange);
                      }}
                      aria-label="Clear status filter"
                    >
                      <IoClose className="h-4 w-4" />
                    </button>
                  </span>
                )}

                {dateRange.from && (
                  <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-200">
                    From: {dateRange.from}
                    <button
                      type="button"
                      className="rounded-full p-1 hover:bg-slate-100 dark:hover:bg-slate-900"
                      onClick={() => handleDateChange('from', '')}
                      aria-label="Clear from date"
                    >
                      <IoClose className="h-4 w-4" />
                    </button>
                  </span>
                )}

                {dateRange.to && (
                  <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-200">
                    To: {dateRange.to}
                    <button
                      type="button"
                      className="rounded-full p-1 hover:bg-slate-100 dark:hover:bg-slate-900"
                      onClick={() => handleDateChange('to', '')}
                      aria-label="Clear to date"
                    >
                      <IoClose className="h-4 w-4" />
                    </button>
                  </span>
                )}

                <button
                  type="button"
                  onClick={clearFilters}
                  disabled={!selectedStatus && !dateRange.from && !dateRange.to}
                  className="ml-auto inline-flex items-center rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-200 dark:hover:bg-slate-900"
                >
                  Reset
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating Filter Button (mobile) */}
      <button
        ref={mobileFilterBtnRef}
        className={[
          'fixed bottom-6 right-6 z-40 inline-flex items-center justify-center rounded-2xl shadow-lg transition',
          'md:hidden',
          showFilters ? 'scale-[0.98]' : 'hover:scale-[1.02]',
          hasFilters
            ? 'bg-sky-600 text-white'
            : 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
        ].join(' ')}
        onClick={toggleFilters}
        aria-label="Toggle filters"
        aria-expanded={showFilters}
        type="button"
      >
        <span className="relative flex h-12 w-12 items-center justify-center">
          {showFilters ? (
            <IoChevronBack className="h-6 w-6" />
          ) : (
            <IoFunnelOutline className="h-6 w-6" />
          )}

          {hasFilters && (
            <span className="absolute -right-2 -top-2 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-white px-2 text-xs font-bold text-sky-700 shadow dark:bg-slate-950 dark:text-sky-300">
              {activeFiltersCount}
            </span>
          )}
        </span>
      </button>

      {/* Overlay */}
      {showFilters && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-[1px] dark:bg-slate-950/60"
          onClick={closeFilters}
          aria-hidden="true"
        />
      )}

      {/* Filters Sidebar */}
      <aside
        ref={filtersSidebarRef}
        className={[
          'fixed right-0 top-0 z-50 h-screen w-[22rem] max-w-[90vw] transform transition-transform duration-200',
          'border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950',
          showFilters ? 'translate-x-0' : 'translate-x-full'
        ].join(' ')}
        aria-label="Article filters"
      >
        {/* Respect fixed navbar height */}
        <div className="h-full pt-10">
          <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
              <div className="min-w-0">
                <h3 className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                  Filter Articles
                </h3>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  Narrow results by status and submission date
                </p>
              </div>

              <button
                className="inline-flex items-center justify-center rounded-xl p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-900 dark:hover:text-slate-100"
                onClick={closeFilters}
                aria-label="Close filters"
                type="button"
              >
                <IoClose className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
              {/* Status */}
              <section className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                <div className="mb-3 flex items-center gap-2">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
                    <IoFilterOutline className="h-5 w-5" />
                  </span>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Status
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Filter by workflow state
                    </p>
                  </div>
                </div>

                <div className="mt-2">
                  <div className="rounded-xl border border-slate-200 bg-white px-2 py-1 focus-within:ring-2 focus-within:ring-sky-500/30 dark:border-slate-800 dark:bg-slate-950">
                    <CustomSelect
                      placeholder="Select status"
                      name="status"
                      value={selectedStatus}
                      onChange={handleStatusChange}
                      options={[
                        { value: '', label: 'All Statuses' },
                        { value: 'submitted', label: 'Submitted' },
                        { value: 'pending', label: 'Pending' },
                        { value: 'under review', label: 'Under Review' },
                        { value: 'approved', label: 'Approved' },
                        { value: 'rejected', label: 'Rejected' }
                      ]}
                    />
                  </div>
                </div>
              </section>

              {/* Date range */}
              <section className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                <div className="mb-3 flex items-center gap-2">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
                    <IoCalendarOutline className="h-5 w-5" />
                  </span>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Submission Date
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Choose a date range
                    </p>
                  </div>
                </div>

                <div className="grid gap-3">
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-950">
                    <DateField
                      label="From"
                      name="from-date"
                      value={dateRange.from}
                      onChange={(e) => handleDateChange('from', e.target.value)}
                      placeholder="From date"
                      maxDate={dateRange.to || undefined}
                      icon={<IoCalendarOutline />}
                    />
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-950">
                    <DateField
                      label="To"
                      name="to-date"
                      value={dateRange.to}
                      onChange={(e) => handleDateChange('to', e.target.value)}
                      placeholder="To date"
                      minDate={dateRange.from || undefined}
                      icon={<IoCalendarOutline />}
                    />
                  </div>
                </div>
              </section>
            </div>

            {/* Footer */}
            <div className="border-t border-slate-200 px-5 py-4 dark:border-slate-800">
              <button
                className={[
                  'w-full rounded-xl px-4 py-3 text-sm font-semibold transition',
                  'focus:outline-none focus:ring-2 focus:ring-sky-500/40',
                  !selectedStatus && !dateRange.from && !dateRange.to
                    ? 'cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-slate-900 dark:text-slate-600'
                    : 'bg-sky-600 text-white hover:bg-sky-700 dark:bg-sky-600 dark:hover:bg-sky-500'
                ].join(' ')}
                onClick={clearFilters}
                disabled={!selectedStatus && !dateRange.from && !dateRange.to}
                type="button"
              >
                Reset Filters
              </button>

              <div className="mt-3 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>{loading ? 'Loading…' : 'Ready'}</span>
                <span>{hasFilters ? `${activeFiltersCount} active` : 'No active filters'}</span>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default ArticleFilters;
